import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, Pencil } from "lucide-react";

import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MarkdownView } from "@/components/shared/MarkdownView";
import { cn } from "@/lib/utils";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  className?: string;
}

export function MarkdownEditor({
  value,
  onChange,
  rows = 5,
  placeholder,
  className,
}: MarkdownEditorProps): JSX.Element {
  const { t } = useTranslation();
  const [previewing, setPreviewing] = useState(false);

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center justify-end gap-1">
        <Button
          type="button"
          variant={previewing ? "outline" : "secondary"}
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          onClick={() => setPreviewing(false)}
        >
          <Pencil className="h-3 w-3" />
          {t("common.edit")}
        </Button>
        <Button
          type="button"
          variant={previewing ? "secondary" : "outline"}
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          onClick={() => setPreviewing(true)}
        >
          <Eye className="h-3 w-3" />
          {t("common.preview")}
        </Button>
      </div>

      {previewing ? (
        <div className="min-h-[80px] rounded-md border border-input bg-background px-3 py-2">
          {value.trim() ? (
            <MarkdownView markdown={value} />
          ) : (
            <p className="text-sm text-muted-foreground italic">
              {t("common.noContent")}
            </p>
          )}
        </div>
      ) : (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}
